import { replaceLabel } from "@/components/Markdown/parse";
import { useToast } from "@/composables/toast";
import { useRefreshFromStore } from "@/stores/refreshFromStore";
import { LazyUndoRedoAction, UndoRedoAction } from "@/stores/undoRedoStore";
import {} from "@/stores/workflowConnectionStore";
import { useWorkflowCommentStore } from "@/stores/workflowEditorCommentStore";
import {} from "@/stores/workflowEditorStateStore";
import { useWorkflowStepStore } from "@/stores/workflowStepStore";
import { assertDefined } from "@/utils/assertions";
import { cloneStepWithUniqueLabel, getLabelSet } from "./cloneStep";
export class LazyMutateStepAction extends LazyUndoRedoAction {
    get name() {
        return this.internalName ?? `modify step ${this.stepLabel}`;
    }
    set name(name) {
        this.internalName = name;
    }
    constructor(stepStore, stepId, key, fromValue, toValue) {
        super();
        Object.defineProperty(this, "key", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fromValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onUndoRedo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stepStore = stepStore;
        this.stepId = stepId;
        this.key = key;
        this.fromValue = fromValue;
        this.toValue = toValue;
        this.stepLabel = `${stepId + 1}`;
        const step = this.stepStore.getStep(stepId);
        if (step) {
            this.stepLabel = `"${stepId + 1}: ${step.label ?? step.name}"`;
        }
    }
    queued() {
        this.stepStore.updateStepValue(this.stepId, this.key, this.toValue);
    }
    changeValue(value) {
        this.toValue = value;
        this.stepStore.updateStepValue(this.stepId, this.key, this.toValue);
    }
    undo() {
        this.stepStore.updateStepValue(this.stepId, this.key, this.fromValue);
        this.onUndoRedo?.();
    }
    redo() {
        this.stepStore.updateStepValue(this.stepId, this.key, this.toValue);
        this.onUndoRedo?.();
    }
}
function onLabelSet(classInstance, from, to) {
    const markdown = classInstance.stateStore.report.markdown ?? "";
    const newMarkdown = replaceLabel(markdown, classInstance.labelType, from, to);
    if (markdown !== newMarkdown) {
        classInstance.stateStore.report.markdown = newMarkdown;
        classInstance.success(`${classInstance.labelTypeTitle} label updated from "${from}" to "${to}" in workflow report.`);
    }
}
export class LazySetLabelAction extends LazyMutateStepAction {
    constructor(stepStore, stateStore, stepId, fromValue, toValue) {
        super(stepStore, stepId, "label", fromValue, toValue);
        Object.defineProperty(this, "labelType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "labelTypeTitle", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "success", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        const step = this.stepStore.getStep(this.stepId);
        assertDefined(step);
        const stepType = step.type;
        const isInput = ["data_input", "data_collection_input", "parameter_input"].indexOf(stepType) >= 0;
        this.labelType = isInput ? "input" : "step";
        this.labelTypeTitle = isInput ? "Input" : "Step";
        this.stateStore = stateStore;
        this.success = useToast().success;
    }
    run() {
        onLabelSet(this, this.fromValue, this.toValue);
    }
    undo() {
        super.undo();
        onLabelSet(this, this.toValue, this.fromValue);
    }
    redo() {
        super.redo();
        this.run();
    }
}
export class LazySetOutputLabelAction extends LazyMutateStepAction {
    constructor(stepStore, stateStore, stepId, fromValue, toValue, toOutputs) {
        const step = stepStore.getStep(stepId);
        assertDefined(step);
        const fromOutputs = structuredClone(step.workflow_outputs);
        super(stepStore, stepId, "workflow_outputs", fromOutputs, structuredClone(toOutputs));
        Object.defineProperty(this, "success", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fromLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "labelType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "output"
        });
        Object.defineProperty(this, "labelTypeTitle", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "Output"
        });
        this.fromLabel = fromValue;
        this.toLabel = toValue;
        this.stateStore = stateStore;
        this.success = useToast().success;
    }
    run() {
        onLabelSet(this, this.fromLabel, this.toLabel);
    }
    undo() {
        super.undo();
        onLabelSet(this, this.toLabel, this.fromLabel);
    }
    redo() {
        this.run();
        super.redo();
    }
}
export class UpdateStepAction extends UndoRedoAction {
    get name() {
        return this.internalName ?? `modify step ${this.stepLabel}`;
    }
    set name(name) {
        this.internalName = name;
    }
    constructor(stepStore, stateStore, stepId, fromPartial, toPartial) {
        super();
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fromPartial", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toPartial", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onUndoRedo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stepStore = stepStore;
        this.stateStore = stateStore;
        this.stepId = stepId;
        this.fromPartial = fromPartial;
        this.toPartial = toPartial;
        this.stepLabel = `${stepId + 1}`;
        const step = this.stepStore.getStep(stepId);
        if (step) {
            this.stepLabel = `"${stepId + 1}: ${step.label ?? step.name}"`;
        }
    }
    isEmpty() {
        return Object.keys(this.fromPartial).length === 0;
    }
    run() {
        const step = this.stepStore.getStep(this.stepId);
        assertDefined(step);
        this.stepStore.updateStep({ ...step, ...this.toPartial });
    }
    undo() {
        const step = this.stepStore.getStep(this.stepId);
        assertDefined(step);
        this.stepStore.updateStep({ ...step, ...this.fromPartial });
        this.onUndoRedo?.();
    }
    redo() {
        this.run();
        this.onUndoRedo?.();
    }
}
export class SetDataAction extends UpdateStepAction {
    constructor(stepStore, stateStore, from, to) {
        const fromPartial = {};
        const toPartial = {};
        Object.entries(from).forEach(([key, value]) => {
            const otherValue = to[key];
            if (JSON.stringify(value) !== JSON.stringify(otherValue)) {
                fromPartial[key] = structuredClone(value);
                toPartial[key] = structuredClone(otherValue);
            }
        });
        super(stepStore, stateStore, from.id, fromPartial, toPartial);
    }
}
export class InsertStepAction extends UndoRedoAction {
    constructor(stepStore, stateStore, stepData) {
        super();
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "updateStepData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "newStepData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stepStore = stepStore;
        this.stateStore = stateStore;
        this.stepData = stepData;
    }
    get name() {
        return `insert ${this.stepData.name}`;
    }
    stepDataToTuple() {
        return Object.values(this.stepData);
    }
    getNewStepData() {
        assertDefined(this.newStepData);
        return this.newStepData;
    }
    run() {
        this.newStepData = this.stepStore.insertNewStep(...this.stepDataToTuple());
        this.stepId = this.newStepData.id;
        if (this.updateStepData) {
            this.stepStore.updateStep(this.updateStepData);
            this.stepId = this.updateStepData.id;
        }
    }
    undo() {
        assertDefined(this.stepId);
        this.stepStore.removeStep(this.stepId);
    }
    redo() {
        this.run();
        assertDefined(this.stepId);
    }
}
export class RemoveStepAction extends UndoRedoAction {
    constructor(stepStore, stateStore, connectionStore, step) {
        super();
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "connectionStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "step", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "connections", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stepStore = stepStore;
        this.stateStore = stateStore;
        this.connectionStore = connectionStore;
        this.step = structuredClone(step);
        this.connections = structuredClone(this.connectionStore.getConnectionsForStep(this.step.id));
    }
    get name() {
        return `remove step "${this.step.id} ${this.step.label ?? this.step.name}"`;
    }
    run() {
        this.stepStore.removeStep(this.step.id);
        this.stateStore.activeNodeId = null;
        this.stateStore.hasChanges = true;
    }
    undo() {
        this.stepStore.addStep(structuredClone(this.step), false, false);
        this.connections.forEach((connection) => this.connectionStore.addConnection(connection));
        this.stateStore.hasChanges = true;
    }
}
export class CopyStepAction extends UndoRedoAction {
    constructor(stepStore, stateStore, step) {
        super();
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "step", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onUndoRedo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stepStore = stepStore;
        this.stateStore = stateStore;
        const labelSet = getLabelSet(stepStore);
        this.stepLabel = `${step.id + 1}: ${step.label ?? step.name}`;
        this.step = cloneStepWithUniqueLabel(step, labelSet);
        delete this.step.id;
    }
    get name() {
        return `duplicate step "${this.stepLabel}"`;
    }
    run() {
        const newStep = this.stepStore.addStep(structuredClone(this.step));
        this.stepId = newStep.id;
        this.stateStore.hasChanges = true;
    }
    undo() {
        assertDefined(this.stepId);
        this.stepStore.removeStep(this.stepId);
    }
}
export class ToggleStepSelectedAction extends UndoRedoAction {
    constructor(stateStore, stepStore, stepId) {
        super();
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toggleTo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stateStore = stateStore;
        this.stepStore = stepStore;
        this.stepId = stepId;
        this.toggleTo = !this.stateStore.getStepMultiSelected(stepId);
        const label = this.stepStore.getStep(this.stepId)?.label;
        this.stepLabel = label ?? `${this.stepId + 1}`;
    }
    get name() {
        if (this.toggleTo === true) {
            return `add step ${this.stepLabel} to selection`;
        }
        else {
            return `remove step ${this.stepLabel} from selection`;
        }
    }
    run() {
        this.stateStore.setStepMultiSelected(this.stepId, this.toggleTo);
    }
    undo() {
        this.stateStore.setStepMultiSelected(this.stepId, !this.toggleTo);
    }
}
export class AutoLayoutAction extends UndoRedoAction {
    constructor(workflowId) {
        super();
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "commentStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "positions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "oldPositions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "workflowId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "ran", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.workflowId = workflowId;
        this.stepStore = useWorkflowStepStore(workflowId);
        this.commentStore = useWorkflowCommentStore(workflowId);
        this.positions = {
            steps: [],
            comments: [],
        };
        this.oldPositions = {
            steps: [],
            comments: [],
        };
        this.ran = false;
    }
    get name() {
        return "auto layout";
    }
    mapPositionsToStore(positions) {
        positions.steps.map((p) => {
            const step = this.stepStore.steps[p.id];
            if (step) {
                this.stepStore.updateStep({
                    ...step,
                    position: {
                        top: p.y,
                        left: p.x,
                    },
                });
            }
        });
        positions.comments.map((c) => {
            const id = parseInt(c.id, 10);
            const comment = this.commentStore.commentsRecord[id];
            if (comment) {
                this.commentStore.changePosition(id, [c.x, c.y]);
                this.commentStore.changeSize(id, [c.w, c.h]);
            }
        });
    }
    async run() {
        this.ran = true;
        this.oldPositions.steps = Object.values(this.stepStore.steps).map((step) => ({
            id: `${step.id}`,
            x: step.position?.left ?? 0,
            y: step.position?.top ?? 0,
        }));
        this.oldPositions.comments = this.commentStore.comments.map((comment) => ({
            id: `${comment.id}`,
            x: comment.position[0],
            y: comment.position[1],
            w: comment.size[0],
            h: comment.size[1],
        }));
        const { autoLayout } = await import(
        /* webpackChunkName: "workflowLayout" */ "@/components/Workflow/Editor/modules/layout");
        this.commentStore.resolveCommentsInFrames();
        this.commentStore.resolveStepsInFrames();
        const newPositions = await autoLayout(this.workflowId, this.stepStore.steps, this.commentStore.comments);
        assertDefined(newPositions);
        this.positions = newPositions;
        if (this.ran) {
            this.mapPositionsToStore(this.positions);
        }
    }
    undo() {
        this.ran = false;
        this.mapPositionsToStore(this.oldPositions);
    }
    redo() {
        this.mapPositionsToStore(this.positions);
    }
}
export function useStepActions(stepStore, undoRedoStore, stateStore, connectionStore) {
    /**
     * If the pending action is a `LazyMutateStepAction` and matches the step id and field key, returns it.
     * Otherwise returns `null`
     */
    function actionForIdAndKey(id, key) {
        const pendingAction = undoRedoStore.pendingLazyAction;
        if (pendingAction instanceof LazyMutateStepAction && pendingAction.stepId === id && pendingAction.key === key) {
            return pendingAction;
        }
        else {
            return null;
        }
    }
    /**
     * Mutates a queued lazy action, if a matching one exists,
     * otherwise creates a new lazy action ans queues it.
     */
    function changeValueOrCreateAction(options) {
        const { step, key, value, name, keepActionAlive, timeout } = options;
        const actionForKey = actionForIdAndKey(step.id, key);
        if (actionForKey) {
            actionForKey.changeValue(value);
            if (keepActionAlive) {
                undoRedoStore.setLazyActionTimeout(timeout);
            }
            if (name) {
                actionForKey.name = name;
            }
            return actionForKey;
        }
        else {
            const actionConstructor = options.actionConstructor ??
                (() => new LazyMutateStepAction(stepStore, step.id, key, step[key], value));
            const action = actionConstructor();
            if (name) {
                action.name = name;
            }
            undoRedoStore.applyLazyAction(action, timeout);
            action.onUndoRedo = () => {
                stateStore.hasChanges = true;
            };
            return action;
        }
    }
    function setPosition(step, position) {
        changeValueOrCreateAction({
            step,
            key: "position",
            value: position,
            name: `move step "${step.id + 1}: ${step.label ?? step.name}"`,
        });
    }
    function setAnnotation(step, annotation) {
        changeValueOrCreateAction({
            step,
            key: "annotation",
            value: annotation,
            name: `edit annotation of step "${step.id + 1}: ${step.label ?? step.name}"`,
        });
    }
    function setOutputLabel(step, workflowOutputs, fromLabel, toLabel) {
        const actionConstructor = () => new LazySetOutputLabelAction(stepStore, stateStore, step.id, fromLabel, toLabel, workflowOutputs);
        changeValueOrCreateAction({
            step,
            key: "workflow_outputs",
            value: workflowOutputs,
            name: `edit output label of step "${step.id + 1}: ${step.label ?? step.name}"`,
            actionConstructor,
            keepActionAlive: true,
            timeout: 2000,
        });
    }
    function setLabel(step, label) {
        const actionConstructor = () => new LazySetLabelAction(stepStore, stateStore, step.id, step.label, label);
        changeValueOrCreateAction({
            step,
            key: "label",
            value: label,
            name: `change label of step ${step.id + 1} to "${label}"`,
            actionConstructor,
            keepActionAlive: true,
            timeout: 2000,
        });
    }
    const { refresh } = useRefreshFromStore();
    function setData(from, to) {
        const action = new SetDataAction(stepStore, stateStore, from, to);
        if (!action.isEmpty()) {
            action.onUndoRedo = () => {
                stateStore.hasChanges = true;
                refresh();
            };
            undoRedoStore.applyAction(action);
        }
    }
    function removeStep(step) {
        const action = new RemoveStepAction(stepStore, stateStore, connectionStore, step);
        undoRedoStore.applyAction(action);
    }
    function updateStep(id, toPartial) {
        const fromStep = stepStore.getStep(id);
        assertDefined(fromStep);
        const fromPartial = {};
        Object.keys(toPartial).forEach((key) => {
            fromPartial[key] = structuredClone(fromStep[key]);
        });
        const action = new UpdateStepAction(stepStore, stateStore, id, fromPartial, toPartial);
        if (!action.isEmpty()) {
            action.onUndoRedo = () => {
                stateStore.hasChanges = true;
                refresh();
            };
            undoRedoStore.applyAction(action);
        }
    }
    function copyStep(step) {
        const action = new CopyStepAction(stepStore, stateStore, step);
        undoRedoStore.applyAction(action);
    }
    return {
        setPosition,
        setAnnotation,
        setLabel,
        setData,
        setOutputLabel,
        removeStep,
        updateStep,
        copyStep,
    };
}
